import { descend, isEmpty, pick, pickBy, prop, sortBy, sortWith, uniqBy } from 'ramda';
import { v4 as uuidv4 } from 'uuid';
import { VISIT_STATUS, flattenQuestions, mappedQuesToAnsT, visitTypes } from 'context/visitContext';
import {
  ACCEPTED_CALL_PATTERN_KEY,
  parsePrescription,
  sessionTStorage,
  TEMP_ADVANCED_PATIENTS_SEARCHES_STORE_KEY,
} from 'utils/utils';
import moment from 'moment';
import {
  Answer,
  AuthToken,
  Patient,
  Prescription,
  QnAType,
  Question,
  QuestionAnswers,
  UnreadMessageType,
} from '../types/types';
import http from '../utils/http';
import uploadAWSS3File from '../utils/uploadAWSS3File';

export async function login(phoneNumber: string, otp: string) {
  if (phoneNumber && !otp) {
    await http.post('/auth/phone/send-code', {
      phoneNumber,
    });
    return 'OTP-SENT';
  }

  const response = await http.post<AuthToken>('/auth/phone/verify-code', {
    phoneNumber,
    verificationCode: otp,
  });

  return response.data;
}

export async function getAuthUser() {
  const response = await http.get('/user/profile');
  return response.data;
}

export async function getPatients(path: string, params?: Record<string, string>) {
  const paramsT = pickBy(value => Boolean(value && !isEmpty(value)), params);
  const response = await http.get(path, { params: paramsT });
  return !response.data ? [] : response.data;
}

export async function getUnreadMessages() {
  const response = await http.get('/conversations/counters');
  return !response.data ? [] : response.data;
}
export async function getConversations(path = '/conversations') {
  const response = await http.get(path);
  return !response.data ? [] : response.data;
}

export async function getQuestionnaires(
  params?: Record<string, string>,
): Promise<QuestionAnswers[]> {
  const paramsT = pickBy(value => Boolean(value && !isEmpty(value)), params);
  const response = await http.get('/questionnaires', { params: paramsT });

  let questionnaires = (response?.data || []) as QuestionAnswers[];

  // We make a check if temp patient search stored, has questionnaires
  const tempPatientSearch = JSON.parse(
    sessionTStorage.get(TEMP_ADVANCED_PATIENTS_SEARCHES_STORE_KEY) || '{}',
  );

  if (Object.keys(tempPatientSearch).length > 0) {
    const tempQuestionnaires = tempPatientSearch?.questionnaires?.filter(
      (fndQuestionnaire: any) => {
        if (!paramsT || Object.keys(paramsT).length === 0) {
          return true;
        }
        return Object.keys(paramsT).every(key => fndQuestionnaire[key] === (paramsT as any)[key]);
      },
    );

    questionnaires = uniqBy(prop('id'), [...questionnaires, ...tempQuestionnaires]);
  }

  questionnaires = questionnaires.filter(
    (fndQuestionnaire: any) =>
      Boolean(fndQuestionnaire.answers.length) &&
      Boolean(visitTypes.find(fndVisitType => fndVisitType.type === fndQuestionnaire.type)),
  );

  questionnaires = sortWith([descend(prop('createdAt'))], questionnaires);

  return questionnaires;
}

export async function getAskQnAs(visitId: string): Promise<QnAType[]> {
  const questionnaires = await getQuestionnaires({ id: visitId });
  let QnAs: QnAType[] = [];

  const questionnaire = questionnaires[0];

  if (
    questionnaire &&
    questionnaire.additionalQuestions &&
    questionnaire.additionalQuestions.length
  ) {
    const getQnAs = await Promise.all(
      questionnaire.additionalQuestions.map(async eachQnA => {
        const entriesQnAs = Object.entries(pick(['question', 'answer'], eachQnA));
        const qnaPromises = entriesQnAs.map(async ([key, value]) => {
          if (value) {
            return {
              id: eachQnA.id,
              content: value,
              createdAt:
                key === 'question' ? eachQnA.createdAt : eachQnA.answeredAt || moment().format(),
              isSender: key === 'question',
            };
          }
          return null;
        });
        return Promise.all(qnaPromises);
      }),
    );

    QnAs = getQnAs.flat().filter(Boolean) as QnAType[];
  }

  if (questionnaire && questionnaire.calls && questionnaire.calls.length) {
    const calls = questionnaire.calls.map(call => {
      const uuid = call.id || call.link.split('/').pop() || call.link;
      return {
        id: `${uuid}${call.acceptedAt ? ` <${ACCEPTED_CALL_PATTERN_KEY}:${call.acceptedAt}>` : ''}`,
        content: call.link,
        createdAt: call.createdAt,
        isSender: true,
      };
    });

    QnAs = [...QnAs, ...calls];
  }

  QnAs = sortBy(prop('createdAt'), QnAs);

  return QnAs;
}

export async function loadReceivedAnswer(visitId: string, QnAsId?: string) {
  const questionnaires = await getQuestionnaires({ id: visitId });

  const additionalQuestion = questionnaires[0]?.additionalQuestions?.find(fndQnA => {
    const today = moment().format('YYYY-MM-DD');
    const qnAAnsweredAt = moment(fndQnA.answeredAt).format('YYYY-MM-DD');
    return (
      fndQnA.id === QnAsId &&
      fndQnA.answer &&
      !fndQnA.answerReadAt &&
      moment(today).isSameOrBefore(qnAAnsweredAt)
    );
  });

  if (!additionalQuestion) return null;

  return {
    id: additionalQuestion.id,
    content: additionalQuestion.answer,
    createdAt: additionalQuestion.answeredAt,
    isSender: false,
  };
}

export async function loadUnAnsweredQnAs(patients?: Array<Patient>) {
  const unAnsweredQnAsPromises = (patients || []).map(async findPatient => {
    const questionnaires = sortBy(
      prop('createdAt'),
      findPatient.questionnaires.filter(
        findQuestionnaire => findQuestionnaire.status === VISIT_STATUS.UNANSWERED,
      ),
    );

    let questionnaire = questionnaires[questionnaires.length - 1];

    if (!questionnaire) return null;

    [questionnaire] = await getQuestionnaires({ id: questionnaire.id });

    if (!questionnaire?.additionalQuestions) return null;

    const additionalQuestions = sortBy(prop('createdAt'), questionnaire?.additionalQuestions);

    const additionalQuestion = additionalQuestions?.[additionalQuestions.length - 1];

    if (questionnaire && additionalQuestion.answer && !additionalQuestion.answerReadAt) {
      return {
        patient: {
          id: findPatient.id,
          name: `${findPatient.firstName} ${findPatient.lastName}`,
        },
        visitId: questionnaire.id,
        QnAs: additionalQuestion,
      };
    }

    return null;
  });

  const unAnsweredQnAs = (await Promise.all(unAnsweredQnAsPromises)).filter(fndUnAnsweredQnAs =>
    Boolean(fndUnAnsweredQnAs && !isEmpty(fndUnAnsweredQnAs)),
  ) as Array<UnreadMessageType>;

  return unAnsweredQnAs;
}

export async function getMedications(params?: Record<string, string>): Promise<Prescription[]> {
  const paramsT = pickBy(value => Boolean(value && !isEmpty(value)), params);

  const [quesResponse, photonResponse] = await Promise.all([
    http.get('/questionnaires', { params: paramsT }),
    http.get(`/integrations/photon/patient-prescriptions/${params?.patientId}`),
  ]);

  let quesPrescriptions = [];

  let photonPrescriptions = [];

  if (quesResponse?.data && quesResponse?.data?.length > 0) {
    const questionnaires = quesResponse.data.filter((fndVisit: any) =>
      Boolean(fndVisit.answers.length),
    ) as QuestionAnswers[];

    quesPrescriptions = questionnaires.reduce((acc: any, questionnaire: QuestionAnswers) => {
      if (!questionnaire.prescriptions || isEmpty(questionnaire.prescriptions)) {
        return acc;
      }

      const accPrescriptions =
        questionnaire.prescriptions?.map((fndPrescription: Prescription, index) => {
          return {
            ...parsePrescription(fndPrescription),
            id: parsePrescription(fndPrescription)?.id || `${questionnaire.id}_${index}`,
            questionnaireId: questionnaire.id,
            visitType: questionnaire.type,
            createdAt: parsePrescription(fndPrescription)?.createdAt || questionnaire.createdAt,
          };
        }) || [];

      return [...acc, ...accPrescriptions];
    }, []);
  }

  photonPrescriptions =
    photonResponse?.data?.map((fndPrescription: any) => {
      return {
        ...parsePrescription(fndPrescription),
        visitType: 'N/A',
      };
    }) || [];

  return sortWith(
    [descend(prop('createdAt') as any)],
    uniqBy(prop('id'), [...quesPrescriptions, ...photonPrescriptions]).filter(fndPrescription =>
      Boolean(fndPrescription.medicationName),
    ),
  );
}

export async function getQuestions(visitType: string) {
  const response = await http.get(`/questionnaires/${visitType}/questions`);
  let questions = !response.data ? [] : response.data;
  questions = uniqBy(prop('id'), flattenQuestions([...questions]));
  return questions;
}

export async function getLabs(params?: Record<string, string>) {
  const paramsT = pickBy(value => Boolean(value && !isEmpty(value)), params);
  const response = await http.get('/lab-tests', { params: paramsT });
  return !response.data ? [] : response.data;
}

export async function getLabTypes() {
  const response = await http.get('/lab-test/types');
  return !response.data ? [] : response.data;
}

export async function getFollowUpRequest(followUps?: Array<Answer>) {
  if (!followUps) {
    return null;
  }

  const response = await http.get(`/questionnaires/FOLLOW-UP/questions`);

  const followUpsQuestions = response?.data as Question[];

  return followUpsQuestions.map(question => {
    return {
      responseQuestion: mappedQuesToAnsT['follow-up'](question, followUps).quesText,
      responseAnswer: mappedQuesToAnsT['follow-up'](question, followUps).quesAnswer,
    };
  });
}

export async function getConversationMessages(
  questionnaireId: string,
  params: Record<string, any>,
) {
  const paramsT = pickBy(value => Boolean(value && !isEmpty(value)), params);
  const response = await http.get(`/conversation/${questionnaireId}`, { params: paramsT });
  return !response.data ? [] : response.data;
}

export async function updateAuthUser(payload: Record<string, any>) {
  const response = await http.patch('/user', payload);
  return response.data;
}

export async function attachPatientNote(patientId: string, payload: Record<string, any>) {
  const response = await http.patch(`/patient/${patientId}/notes`, payload);
  return response.data;
}

export async function updatePatientMedHistory(patientId: string, payload: Record<string, any>) {
  const response = await http.patch(`/patient/${patientId}/medical-history`, payload);
  return response.data;
}

export async function syncPatientWithPhoton(patientId: string) {
  const response = await http.post(`/integrations/photon/sync-patient/${patientId}`);
  return response.data;
}

export async function sendLabRequest(payload: Record<string, any>) {
  const response = await http.post('/lab-test', payload);
  return response.data;
}

export async function updateLabRequest(id: string, payload: Record<string, any>) {
  const response = await http.patch(`/lab-test/${id}`, payload);
  return response?.data;
}

export async function updateQuestionnaire(questionnaireId: string, payload: Record<string, any>) {
  const response = await http.patch(`/questionnaire/${questionnaireId}`, payload);
  return response.data;
}

export async function sendMessage(questionnaireId: string, payload: Record<string, any>) {
  let fndPayload = { ...payload };

  if (payload.content instanceof File && ['IMAGE', 'FILE'].includes(payload.type)) {
    const filename = payload.content.name;
    const fileType = payload.content.type || 'image/jpeg';
    const fileKey = `chat/${uuidv4()}/${filename}`;

    fndPayload = {
      ...fndPayload,
      content: await uploadAWSS3File(payload.content, fileKey, fileType, 'public'),
    };
  }

  const response = await http.post(`/conversation/${questionnaireId}/message`, fndPayload);
  return response.data;
}

export async function sendPingMessage(questionnaireId: string) {
  const response = await http.post(`/conversation/${questionnaireId}/ping`);
  return response.data;
}

export async function startVideoCall(questionnaireId: string) {
  const response = await http.post(`/questionnaire/${questionnaireId}/call`);
  return response.data;
}

export async function markConversationRead(questionnaireId: string) {
  const response = await http.patch(
    `/conversation/${questionnaireId}/read?questionnaire_id=${questionnaireId}`,
  );
  return response?.data ? 'MARKED' : 'UN-MARKED';
}

export async function syncNotificationToken(payload: Record<string, any>) {
  const response = await http.patch('/user/notifications', payload);
  return response.data;
}

export async function deregisterNotification(payload: Record<string, any>) {
  const response = await http.delete('/user/notifications', { data: payload });
  return response.data;
}
